import { motion } from "framer-motion";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from "recharts";
import { SectionHeader } from "./SectionHeader";
import { inrCompact, inrFull, CAT_COLORS } from "@/lib/format";

const EASE = [0.16, 1, 0.3, 1];
const FY_COLORS = ["#0F172A", "#D4AF37", "#0EA5E9", "#9D4EDD"];

const Panel = ({ children, className = "", delay = 0, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
    data-testid={testId}
    className={`bg-white border border-slate-200 p-6 shadow-sm ${className}`}
  >
    {children}
  </motion.div>
);

const MoneyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 shadow-md px-4 py-3 text-xs">
      <p className="font-semibold text-slate-900 mb-2">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2 text-slate-600 tabular-nums">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          {p.name}: <span className="font-semibold text-slate-900">{inrFull(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

const axisProps = { tick: { fontSize: 11, fill: "#64748B" }, tickLine: false };

const PctTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 shadow-md px-4 py-3 text-xs">
      <p className="font-semibold text-slate-900 mb-2">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2 text-slate-600 tabular-nums">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          {p.name}: <span className="font-semibold text-slate-900">{Number(p.value).toFixed(1)}%</span>
        </p>
      ))}
    </div>
  );
};

export const TrendsGrid = ({ data }) => {
  const monthly = data?.monthly || [];
  const fyMonthly = data?.fy_monthly || [];
  const coinMixFy = data?.coin_mix_fy || [];
  const fys = data?.meta?.fys || [];
  const mix = monthly.map((m) => {
    const t = (m.coins || 0) + (m.excl_coins || 0);
    return {
      label: m.label,
      Coins: t ? +(((m.coins || 0) / t) * 100).toFixed(1) : 0,
      "Non-Coins": t ? +(((m.excl_coins || 0) / t) * 100).toFixed(1) : 0,
    };
  });
  const cats = data?.categories;
  const pieData = cats
    ? [
        { name: "Gold", key: "gold", value: Math.max(0, cats.gold) },
        { name: "Diamond & Colour Stone", key: "diamond", value: Math.max(0, cats.diamond) },
        { name: "Precious & Uncut", key: "precious", value: Math.max(0, cats.precious) },
        { name: "Silver", key: "silver", value: Math.max(0, cats.silver) },
      ]
    : [];
  const catTotal = pieData.reduce((s, x) => s + x.value, 0);

  return (
    <section id="trends" data-testid="trends-section" className="bg-slate-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14">
        <SectionHeader number="02" title="Trends" subtitle="Momentum &amp; mix." />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Panel delay={0.1} testId="category-breakdown-chart">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-2">
              Category Breakdown
            </p>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={82} paddingAngle={2} strokeWidth={0}>
                    {pieData.map((x) => (
                      <Cell key={x.key} fill={CAT_COLORS[x.key]} />
                    ))}
                  </Pie>
                  <Tooltip content={<MoneyTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2.5 mt-2">
              {pieData.map((x) => (
                <div key={x.key} className="flex items-center justify-between text-xs" data-testid={`category-row-${x.key}`}>
                  <span className="flex items-center gap-2 text-slate-600">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: CAT_COLORS[x.key] }} />
                    {x.name}
                  </span>
                  <span className="font-semibold text-slate-900 tabular-nums">
                    {inrCompact(x.value)}
                    <span className="text-slate-400 font-normal ml-1.5">
                      {catTotal ? `${((x.value / catTotal) * 100).toFixed(1)}%` : ""}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel className="lg:col-span-2" delay={0.05} testId="fy-stacked-chart">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-6">
              Multi-Year Monthly Turnover (Apr–Mar, stacked by FY)
            </p>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={fyMonthly} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="month" {...axisProps} axisLine={{ stroke: "#E2E8F0" }} />
                  <YAxis tickFormatter={inrCompact} {...axisProps} axisLine={false} width={76} />
                  <Tooltip content={<MoneyTooltip />} cursor={{ fill: "#F8FAFC" }} />
                  <Legend formatter={(v) => <span className="text-xs text-slate-600">{v}</span>} />
                  {fys.map((f, i) => (
                    <Bar key={f} dataKey={f} stackId="fy" fill={FY_COLORS[i % FY_COLORS.length]} maxBarSize={44} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel delay={0.15} testId="studded-trend-chart">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-6">
              Studded Composition % (monthly)
            </p>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthly} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" {...axisProps} axisLine={{ stroke: "#E2E8F0" }} />
                  <YAxis tickFormatter={(v) => `${v}%`} {...axisProps} axisLine={false} width={44} />
                  <Tooltip
                    formatter={(v) => [`${Number(v).toFixed(2)}%`, "Studded"]}
                    contentStyle={{ fontSize: 12, border: "1px solid #E2E8F0" }}
                  />
                  <Line type="monotone" dataKey="studded" name="Studded %" stroke="#9D4EDD" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <Panel className="lg:col-span-2" delay={0.05} testId="coin-mix-chart">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-6">
              Coins vs Non-Coins Mix (100% stacked)
            </p>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mix} margin={{ top: 5, right: 5, left: 0, bottom: 0 }} stackOffset="expand">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" {...axisProps} axisLine={{ stroke: "#E2E8F0" }} />
                  <YAxis tickFormatter={(v) => `${Math.round(v * 100)}%`} {...axisProps} axisLine={false} width={44} />
                  <Tooltip content={<PctTooltip />} cursor={{ fill: "#F8FAFC" }} />
                  <Legend formatter={(v) => <span className="text-xs text-slate-600">{v}</span>} />
                  <Bar dataKey="Non-Coins" stackId="mix" fill="#0F172A" maxBarSize={30} />
                  <Bar dataKey="Coins" stackId="mix" fill="#D4AF37" maxBarSize={30} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel delay={0.1} testId="coin-share-fy-chart">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-6">
              Coin Share % — YoY by FY (Apr–Mar)
            </p>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={coinMixFy} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="month" {...axisProps} axisLine={{ stroke: "#E2E8F0" }} />
                  <YAxis tickFormatter={(v) => `${v}%`} {...axisProps} axisLine={false} width={44} />
                  <Tooltip content={<PctTooltip />} />
                  <Legend formatter={(v) => <span className="text-xs text-slate-600">{v}</span>} />
                  {fys.map((f, i) => (
                    <Line key={f} type="monotone" dataKey={f} stroke={FY_COLORS[i % FY_COLORS.length]} strokeWidth={2} dot={{ r: 2.5 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          {monthly.some((m) => m.closing_stock != null) && (
            <Panel className="lg:col-span-2" delay={0.1} testId="closing-stock-chart">
              <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-6">
                Closing Stock Value (monthly, latest per location)
              </p>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthly} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gCs" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="label" {...axisProps} axisLine={{ stroke: "#E2E8F0" }} />
                    <YAxis tickFormatter={inrCompact} {...axisProps} axisLine={false} width={76} />
                    <Tooltip content={<MoneyTooltip />} />
                    <Area type="monotone" dataKey="closing_stock" name="Closing Stock" stroke="#0EA5E9" strokeWidth={2} fill="url(#gCs)" connectNulls />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          )}
        </div>
      </div>
    </section>
  );
};
